import React from 'react';
import { Heart, Users, Compass, Lightbulb } from 'lucide-react';

const Philosophy = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1711478318247-eabfa50cd4f8"
          alt="Atmospheric nature"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
            How We Explore
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Learning, creating, and discovering together
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-2xl font-light text-gray-800 leading-relaxed mb-8">
            Light Chasing Adventures exists because extraordinary experiences shouldn't require luxury pricing, technical expertise, or influencer-perfect Instagram feeds.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            We're here for the people who are curious, who want to learn by doing, and who believe that creativity and confidence grow through shared discovery — not perfection.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">
            What We Believe
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-mint/30 rounded-full mb-4">
                <Lightbulb className="text-brand-teal" size={24} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Photography is About Noticing
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Technical settings come second to actually seeing what's in front of you. Light, composition, atmosphere — these matter more than the latest gear or complex techniques. We teach you to notice first, adjust settings second.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-mint/30 rounded-full mb-4">
                <Users className="text-brand-teal" size={24} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Small Groups, Big Impact
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Maximum 4-6 people. This isn't about herding crowds or competing for shots. It's about creating space for questions, experimentation, and genuine learning. Everyone gets attention. Everyone progresses.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-purple/20 rounded-full mb-4">
                <Heart className="text-brand-purple" size={24} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Accessible, Not Exclusive
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We price our trips honestly. No luxury markup, no hidden costs, no feeling like you need to be wealthy to experience extraordinary places. These adventures are designed to be within reach for curious people, not just the already-comfortable.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-mint/30 rounded-full mb-4">
                <Compass className="text-brand-teal" size={24} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Experience Over Expertise
              </h3>
              <p className="text-gray-600 leading-relaxed">
                You don't need to be an expert to join us. Beginners are welcome — encouraged, even. We're not here to showcase our knowledge; we're here to share it. Learning happens through doing, asking questions, and trying things together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Founder's Approach */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://images.unsplash.com/photo-1628946329352-4b4e9fcbfa6a"
                alt="Highland landscape"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6">
                A Fellow Adventurer
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                I'm not here to be an untouchable expert on a pedestal. I'm a steady, experienced guide who loves creating safe, inclusive environments where people feel comfortable asking questions and trying new things.
              </p>
              <p className="text-gray-600 leading-relaxed">
                I believe photography is a tool for noticing the world more deeply. And I believe that standing under the Northern Lights or walking through a Highland glen should be experiences that transform you — not empty box-ticking for social media.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We're Not */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            What We're Not
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border-l-4 border-brand-teal">
              <p className="text-gray-700 leading-relaxed">
                <strong>Not a luxury tour company.</strong> We don't charge luxury prices or promise five-star everything. Comfortable lodging, yes. Gold-plated taps, no. The experience is the luxury.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border-l-4 border-brand-teal">
              <p className="text-gray-700 leading-relaxed">
                <strong>Not gear-focused.</strong> You don't need the latest camera or a bag full of lenses. We work with what you have and teach you to use it well. Gear matters far less than you've been told.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border-l-4 border-brand-teal">
              <p className="text-gray-700 leading-relaxed">
                <strong>Not influencer travel.</strong> This isn't about getting 'the shot' for Instagram. It's about actually experiencing places — slowing down, noticing, and being present. The photos come from that, not the other way around.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border-l-4 border-brand-teal">
              <p className="text-gray-700 leading-relaxed">
                <strong>Not exclusive or intimidating.</strong> You don't need prior experience, expensive equipment, or to feel like the 'right kind' of photographer. If you're curious and willing to learn, you belong here.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            How We Learn Together
          </h2>
          <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              <strong>By doing, not just listening.</strong> We don't spend hours in classrooms. We're outside, in the landscape, with cameras in hand. You learn by trying things, making mistakes, and adjusting. That's how it sticks.
            </p>
            <p>
              <strong>Through conversation and collaboration.</strong> The best learning happens when people share what they're seeing, ask questions, and help each other notice things. It's not one-way instruction — it's shared discovery.
            </p>
            <p>
              <strong>With patience and without judgment.</strong> There are no stupid questions. No 'wrong' way to see something. Confidence grows when you feel safe to experiment, and that's the environment we create.
            </p>
            <p>
              <strong>By understanding, not memorizing.</strong> We teach the 'why' behind camera settings and composition principles. When you understand how light works and why certain choices matter, you can apply that knowledge anywhere — not just repeat steps by rote.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-6">
            Does This Resonate?
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            If you're curious, willing to learn, and want to experience extraordinary places without the luxury price tag or intimidation factor, we'd love to have you join us.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Philosophy;